import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import { USAGE_LIMITS_I18N } from "features/usage-config/constants/usage-meters";
import { cn } from "lib/utils";

const T = USAGE_LIMITS_I18N;

export interface UsageCardFrameProps {
  cardId: string;
  label: string;
  dimmed?: boolean;
  children: ReactNode;
}

export const UsageCardFrame = ({ cardId, label, dimmed, children }: UsageCardFrameProps) => (
  <section
    role="region"
    aria-label={label}
    data-usage-card={cardId}
    data-dimmed={dimmed ? "true" : "false"}
    className={cn("flex flex-col gap-3 rounded-sm border bg-card p-4", dimmed && "opacity-60")}
  >
    {children}
  </section>
);

export interface UsageFieldOriginProps {
  /** Payload field this chip belongs to, so a tab can tell one origin from another. */
  field?: string;
  /** Undefined on the tenant tab, where the field is not inherited from anywhere. */
  inherited?: boolean;
  onFollowTenant?: () => void;
  readOnly?: boolean;
}

export const UsageFieldOrigin = ({
  field,
  inherited,
  onFollowTenant,
  readOnly,
}: UsageFieldOriginProps) => {
  const { t } = useTranslation();

  if (inherited === undefined) return null;

  return (
    <div className="flex items-center gap-2" data-field={field}>
      <Badge variant={inherited ? "outline" : "secondary"}>
        {inherited ? t(T.INHERITED_FROM_TENANT) : t(T.OVERRIDDEN_HERE)}
      </Badge>
      {!inherited && onFollowTenant && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onFollowTenant}
          disabled={readOnly}
        >
          {t(T.FOLLOW_TENANT)}
        </Button>
      )}
    </div>
  );
};
